import React from 'react';
import styled from 'styled-components';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { Paper, List, ListItem, ListItemText, ListItemIcon } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Mood from '@material-ui/icons/Mood';
import { Check, Receipt } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';

import Pricing from './pricing';
import { HeroIllustration, ReachIllustration } from './illustrations';

const Body = styled.div`
  width: 100%;
`;

const useStyles = makeStyles((theme) => ({
  header: {
    background: 'linear-gradient(90deg, #FF8A75 0%, #FF6B6B 55%, #E8484F 100%)',
    minHeight: '250px',
    borderBottomLeftRadius: '30px',
    borderBottomRightRadius: '30px',
    boxShadow: '0 18px 60px rgba(232, 72, 79, 0.18)',
  },
  hero: {
    paddingTop: '6rem',
    paddingBottom: '6rem',
    color: 'var(--color-text-light)',
  },
  heroPaper: {
    padding: '2rem 1rem',
    [theme.breakpoints.down('xs')]: {
      padding: '1rem',
    },
  },
  heroBadge: {
    display: 'inline-block',
    padding: '0.4rem 0.9rem',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.18)',
    color: 'var(--color-text-light)',
    fontSize: '0.85rem',
    fontWeight: 500,
    marginBottom: '1.2rem',
    backdropFilter: 'blur(4px)',
  },
  actionBox: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  heroImage: {
    width: '100%',
    height: 'auto',
    maxWidth: '500px',
    display: 'block',
    margin: 'auto',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '320px',
      marginTop: '1.5rem',
    },
  },
  section: {
    paddingTop: '5rem',
    paddingBottom: '5rem',
  },
  sectionAlt: {
    paddingTop: '5rem',
    paddingBottom: '5rem',
    backgroundColor: 'var(--color-background-white)',
  },
  sectionTinted: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
    backgroundColor: 'var(--color-background-grey)',
  },
  sectionColumn: {
    paddingRight: '2rem',
    paddingLeft: '2rem',
  },
  sectionIllustration: {
    width: '100%',
    height: 'auto',
    maxWidth: '460px',
    display: 'block',
    margin: 'auto',
  },
  statsBanner: {
    background: 'linear-gradient(90deg, #FF8A75 0%, #FF6B6B 55%, #E8484F 100%)',
    paddingTop: '4rem',
    paddingBottom: '4rem',
    color: 'var(--color-text-light)',
  },
  statItem: {
    textAlign: 'center',
    padding: '1rem',
    '& > h2': {
      color: 'var(--color-text-light)',
      fontSize: '2.6rem',
      marginBottom: '0.4rem',
    },
    '& > span': {
      display: 'block',
      fontSize: '0.95rem',
      opacity: 0.9,
      letterSpacing: '0.02em',
    },
  },
  paper: {
    padding: '4rem',
    backgroundColor: 'var(--color-accent-prim)',
    color: 'var(--color-light-grey)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lift)',
    [theme.breakpoints.down('xs')]: {
      padding: '2.4rem',
    },
  },
  peppolCard: {
    padding: '2.5rem',
    borderRadius: 'var(--radius-lg)',
    backgroundColor: 'var(--color-background-white)',
    boxShadow: 'var(--shadow-soft)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1.5rem',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      padding: '2rem',
      gap: '1rem',
    },
  },
  peppolIcon: {
    flexShrink: 0,
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-pink-blush)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: '32px',
      color: 'var(--color-coral-deep)',
    },
  },
  peppolBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.7rem',
    borderRadius: '999px',
    background: 'var(--color-pink-blush)',
    color: 'var(--color-coral-deep)',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '0.6rem',
  },
  smiley: {
    '& > * > *': {
      fontSize: '60px',
      fill: 'var(--color-accent-prim)',
    },
  },
  icon: {
    color: 'var(--color-accent-prim)',
  },
  link: {
    color: 'var(--color-text-white)',
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}));

const benefits = [
  'Ontmoet nieuwe klanten en bied je cadeaubonnen online aan.',
  'Alles zelf in beheer: je kiest zelf hoeveel en voor welk bedrag je cadeaubonnen verkoopt.',
  'Via de webshop kunnen klanten beveiligd afrekenen en ontvangen ze hun cadeaubon onmiddellijk via e-mail.',
  'Het platform is volledig gratis, je betaalt geen maandelijkse of andere kosten. Je betaalt enkel €2,99 per verkochte bon. Niets verkocht = dus ook geen kosten!',
  'Spring mee op de jaarlijkse groei in verkoop van online cadeaubonnen.',
  'Makkelijke koppeling met je boekhouding en geen fysieke bonnen meer die je 10 jaar moet archiveren.',
  'Maandelijks krijg je een detailoverzicht en worden alle verkochte cadeaubonnen doorgestort naar jou.',
];

export default function About() {
  const history = useHistory();
  const classes = useStyles();

  return (
    <Body>
      <div className={classes.header}>
        <section className={classes.hero}>
          <Container maxWidth="lg">
            <div className={classes.heroPaper}>
              <Grid container spacing={3} alignItems="center">
                <Grid item sm={12} md={6}>
                  <Box textAlign="left">
                    <span className={classes.heroBadge}>✨ Voor lokale winkels & handelaars</span>
                    <h1>Jouw cadeaubonnen op onze webshop?</h1>
                    <Typography variant="subtitle1" color="inherit" paragraph>
                      Start vandaag nog met het verkopen van jouw cadeaubonnen via onze webshop. Registreer je profiel gratis, voeg je cadeaubon toe en that&apos;s it!
                    </Typography>
                    <Typography variant="subtitle1" color="inherit" paragraph>
                      Daarna kan je via je eigen gepersonaliseerde dashboard je bonnen beheren, inwisselen en zo veel meer.
                    </Typography>
                    <Typography variant="subtitle1" color="inherit" paragraph>
                      <a className={classes.link} href="https://stallesbon.blob.core.windows.net/public/Brochure.pdf" target="_blank" rel="noreferrer">Bekijk hier onze brochure voor meer info!</a>
                    </Typography>
                    <Box mt={3} className={classes.actionBox}>
                      <Button style={{ marginRight: '10px' }} variant="contained" className="customButton2" onClick={() => window.open('https://app.allesbon.be')}>REGISTREER</Button>
                      <Button variant="outlined" className="customButton3" onClick={() => history.push('/demo')}>DEMO</Button>
                    </Box>
                  </Box>
                </Grid>
                <Grid item sm={12} md={6}>
                  <HeroIllustration className={classes.heroImage} />
                </Grid>
              </Grid>
            </div>
          </Container>
        </section>
      </div>

      <section className={classes.section}>
        <Container maxWidth="md">
          <Grid container alignItems="center">
            <Grid item xs={12} sm={6} className={classes.sectionColumn}>
              <Box textAlign="left">
                <h2>Vergroot je online bereik</h2>
                <Typography variant="subtitle1" color="textSecondary" paragraph>
                  Het online koopgedrag groeit elke dag en zal zeker nog exponentieel blijven groeien. Profiteer van een groter bereik door jouw winkel of onderneming op onze webshop te plaatsen en jouw cadeaubonnen eenvoudig vanaf daar te verkopen. Dit alles kan binnen de dag al opgestart worden! Waar wacht je nog op?
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.sectionColumn}>
              <ReachIllustration className={classes.sectionIllustration} />
            </Grid>
          </Grid>
        </Container>
      </section>

      <section className={classes.sectionTinted}>
        <Container maxWidth="md">
          <Grid container>
            <Grid item xs={12} sm={6} className={classes.sectionColumn}>
              <Box textAlign="left">
                <h2>Alles-in-1 dashboard</h2>
                <Typography variant="subtitle1" color="textSecondary" paragraph>
                  Ons dashboard vereenvoudigt administratie en vermijdt gebruikersfouten, onoverzichtelijke Excellijsten, complexe boekhouding, vergetelheden van personeel, etc. Het is super eenvoudig om cadeaubonnen op te zoeken, in te wisselen of een volledige historiek te raadplegen.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} className={classes.sectionColumn}>
              <Box textAlign="left">
                <Typography variant="subtitle1" color="textSecondary" paragraph>
                  Deel correcte informatie eenvoudig met je boekhouder. Altijd een juiste up-to-date lijst in slechts enkele klikken. Rekenfouten worden eenvoudig uitgesloten: het systeem rekent zelf uit hoeveel er werd gebruikt van de bon, hoeveel er nog opstaat en hoeveel de klant eventueel nog moet opleggen.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </section>

      <section className={classes.section}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <h2>Tevreden klanten</h2>
            <Typography variant="subtitle1" color="textSecondary" paragraph>
              Klanten kunnen 24/7 cadeaubonnen aankopen en genieten van het gemak om deze digitaal ter beschikking te hebben. Geen onnodig afdruk meer en dat is voor iedereen fijn! Verder kunnen zij hun cadeaubon in verschillende keren opgebruiken en eventueel ook eenmalig verlengen.
            </Typography>
            <IconButton className={classes.smiley}>
              <Mood />
            </IconButton>
          </Box>
        </Container>
      </section>

      <section className={classes.sectionAlt}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <h2>Alle voordelen op een rijtje</h2>
            <List>
              {benefits.map((text) => (
                <ListItem key={text}>
                  <ListItemIcon className={classes.icon}>
                    <Check />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Container>
      </section>

      <section className={classes.section}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <h2>Prijzen</h2>
            <Typography variant="subtitle1" color="textSecondary" paragraph>
              Betaal enkel een vast bedrag per cadeaubon. Volledig transparant en geen verrassende kosten!
            </Typography>
          </Box>
          <Pricing />
          <Box textAlign="center">
            <Typography variant="subtitle1" color="textSecondary" paragraph>Elk moment opzegbaar 😉</Typography>
          </Box>
        </Container>
      </section>

      <section className={classes.sectionTinted}>
        <Container maxWidth="md">
          <div className={classes.peppolCard}>
            <div className={classes.peppolIcon}>
              <Receipt />
            </div>
            <Box textAlign="left">
              <span className={classes.peppolBadge}>E-invoicing</span>
              <h2>Facturatie via Peppol</h2>
              <Typography variant="subtitle1" color="textSecondary" paragraph>
                Al onze facturen versturen we automatisch via het Peppol-netwerk. Zo ontvang jij je facturen rechtstreeks en veilig in je boekhoudsoftware — geen losse PDF&apos;s, geen manuele invoer, geen gedoe.
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Klaar voor de Belgische e-invoicing verplichting, vandaag al volledig in orde.
              </Typography>
            </Box>
          </div>
        </Container>
      </section>

      <section className={classes.section}>
        <Container maxWidth="md">
          <Paper className={classes.paper}>
            <Box textAlign="center">
              <h2>Start vandaag nog met de verkoop van jouw cadeaubon</h2>
              <Typography variant="subtitle1" paragraph>
                Meer weten over de mogelijkheden of op zoek naar een oplossing op maat? Wij staan voor je klaar.
              </Typography>
            </Box>
            <Box mt={3} textAlign="center">
              <Button style={{ marginRight: '10px' }} className="customButton2" onClick={() => history.push('/contact')}>Contact</Button>
              <Button variant="outlined" className="customButton3" onClick={() => history.push('/demo')}>DEMO</Button>
            </Box>
          </Paper>
        </Container>
      </section>
    </Body>
  );
}
